import { DIVIDER_CDT_LEVELS, type DividerCdtLevel } from "@CommonTypes/backend/PspRules";
import type { ResponseGetCdtCategoryTree } from "@CommonApi/cdt/categories/_categoryId_/tree";
import type { CdtTreeItem } from "@CommonApi/cdt/trees/_treeId_/items";
import type {
  PlanogramItemDimensionFields,
  PlanogramItemOrientation,
} from "@CommonTypes/merchflow/pog/item";
import type { PlanogramShelfDividers } from "@CommonTypes/merchflow/pog/shelf";
import type {
  DividerAttributeValue,
  DividerBasePog,
  DividerPolicyProduct,
} from "@CommonUtils/pog/dividerPolicy";

/** A product's value on each CDT level, as the rules window groups it. */
export type TellOGroupValues = Partial<Record<DividerCdtLevel, string | null | undefined>>;

/** The rules window's product (its `DividerRulesProduct`), all Tell O reads of it. */
export interface TellOProduct extends DividerPolicyProduct {
  name?: string | null;
  /** The category's CDT tree when it places any product, else absent (`cdt` is read). */
  groupValues?: TellOGroupValues;
}

/** The category's own name for each CDT level, where the CDT pages show one. */
export type TellOLevelNames = Partial<Record<DividerCdtLevel, string | null>>;

/** The base planogram's items as the product list reads them; a non-circular pog fits. */
export interface TellOBasePog extends DividerBasePog {
  planogram?: {
    bays?: readonly {
      shelves?: readonly {
        type?: string;
        width?: number;
        depth?: number;
        notchNo?: number;
        dividers?: PlanogramShelfDividers | null;
        loaderDepth?: number | null;
        items?: readonly ({
          productCode: string;
          facings?: number | null;
          name?: string | null;
          variant?: string | null;
          cdt1?: string | null;
          cdt2?: string | null;
          cdt3?: string | null;
          /** With the measurements below, what one facing's width is read from. */
          orientation?: PlanogramItemOrientation | null;
          validOrientations?: PlanogramItemOrientation[] | null;
        } & Partial<PlanogramItemDimensionFields>)[];
      }[];
    }[];
  } | null;
}

/** The category's CDT tree as far as grouping reads it (GET /cdt/categories/:categoryId/tree). */
export interface TellOCdtTree {
  nodes: readonly Pick<
    ResponseGetCdtCategoryTree["nodes"][number],
    "id" | "level" | "name" | "parentNodeId" | "subdivisionField"
  >[];
}

/** "Level 1".."Level 4" unless the category names the level. */
export const tellOLevelLabel = (level: DividerCdtLevel, names: TellOLevelNames = {}) =>
  names[level]?.trim() || `Level ${DIVIDER_CDT_LEVELS.indexOf(level) + 1}`;

/** The value grouping reads for a product on one level: the tree first. */
export const tellOGroupValue = (product: TellOProduct, level: DividerCdtLevel) =>
  String((product.groupValues ?? product.cdt)?.[level] ?? "").trim();

/**
 * Each product's value on every CDT level from the category's CDT tree: its
 * node's name on the node's level, and its ancestors' names on theirs.
 */
export const tellOCdtValuesFromTree = (
  tree: TellOCdtTree | null | undefined,
  items: readonly Pick<CdtTreeItem, "sku" | "nodeId">[],
): Map<string, TellOGroupValues> => {
  const nodes = new Map((tree?.nodes ?? []).map((node) => [node.id, node]));
  const result = new Map<string, TellOGroupValues>();
  for (const item of items) {
    if (item.nodeId === null || !item.sku || result.has(item.sku)) continue;
    const values: TellOGroupValues = {};
    const seen = new Set<number>();
    let node = nodes.get(item.nodeId);
    while (node && !seen.has(node.id)) {
      seen.add(node.id);
      const level = DIVIDER_CDT_LEVELS[node.level];
      if (level && values[level] === undefined) values[level] = node.name;
      node = node.parentNodeId === null ? undefined : nodes.get(node.parentNodeId);
    }
    if (Object.keys(values).length > 0) result.set(item.sku, values);
  }
  return result;
};

/** The subdivision field of the first node on each level, as the CDT tree editor shows it. */
export const tellOLevelNamesFromTree = (
  tree: TellOCdtTree | null | undefined,
): TellOLevelNames => {
  const names: TellOLevelNames = {};
  for (const node of tree?.nodes ?? []) {
    const level = DIVIDER_CDT_LEVELS[node.level];
    const name = node.subdivisionField?.trim();
    if (level && name && !names[level]) names[level] = name;
  }
  return names;
};

/** Base products in wall order, then the category's others; with a CDT tree, grouping reads the tree. */
export const tellOProducts = (
  basePog: TellOBasePog | null | undefined,
  attributesByProductCode: ReadonlyMap<string, Readonly<Record<string, DividerAttributeValue>>>,
  namesByProductCode: ReadonlyMap<string, string | null> = new Map(),
  treeValuesByProductCode: ReadonlyMap<string, TellOGroupValues> = new Map(),
): TellOProduct[] => {
  const byTree = treeValuesByProductCode.size > 0;
  const treeValues = (productCode: string) =>
    byTree ? { groupValues: treeValuesByProductCode.get(productCode) ?? {} } : {};
  const products = new Map<string, TellOProduct>();
  for (const bay of basePog?.planogram?.bays ?? []) {
    for (const shelf of bay.shelves ?? []) {
      for (const item of shelf.items ?? []) {
        if (products.has(item.productCode)) continue;
        products.set(item.productCode, {
          productCode: item.productCode,
          name: item.name || namesByProductCode.get(item.productCode) || null,
          cdt: { cdt0: item.variant, cdt1: item.cdt1, cdt2: item.cdt2, cdt3: item.cdt3 },
          attributes: attributesByProductCode.get(item.productCode),
          ...treeValues(item.productCode),
        });
      }
    }
  }
  for (const [productCode, attributes] of attributesByProductCode) {
    if (products.has(productCode)) continue;
    products.set(productCode, {
      productCode,
      name: namesByProductCode.get(productCode) ?? null,
      attributes,
      ...treeValues(productCode),
    });
  }
  return [...products.values()];
};
